import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Form, Space, Typography } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormFields from '../../../themes/admin/Fields/FormFields';
import Modal from '../../../themes/default/Modal';
import useValidator from '../../../utilities/useValidator';
import { DELETE_CONFIRMATION_TEXT } from '../../../utilities/validators';
import { deleteDealerValidator } from './useDealerValidators';

type DeleteDealerModalValues = {
    confirmValue: string;
};

type DeleteDealerModalProps = {
    onClose: () => void;
    open: boolean;
};

const initialValues: DeleteDealerModalValues = {
    confirmValue: '',
};

const DeleteDealerModal = ({ onClose, open }: DeleteDealerModalProps) => {
    const { t } = useTranslation(['dealerForm']);
    const { values, resetForm } = useFormikContext<DeleteDealerModalValues>();

    const handleClose = useCallback(() => {
        onClose();
        resetForm();
    }, [onClose, resetForm]);

    const isMatchingConfirmation = useMemo(
        () => values.confirmValue !== DELETE_CONFIRMATION_TEXT,
        [values.confirmValue]
    );

    return (
        <Modal
            cancelText={t('dealerForm:deleteModal.cancelText')}
            okButtonProps={{
                htmlType: 'submit',
                form: 'deleteDealerModalForm',
                disabled: isMatchingConfirmation,
            }}
            okText={t('dealerForm:deleteModal.okText')}
            onCancel={handleClose}
            open={open}
            title={
                <Space align="center" size="middle">
                    <ExclamationCircleOutlined style={{ color: '#FF4D4F' }} />
                    <span>{t('dealerForm:deleteModal.title')}</span>
                </Space>
            }
        >
            <>
                <Typography>{t('dealerForm:deleteModal.content')}</Typography>
                <FormFields.InputField
                    label={t('dealerForm:deleteModal.confirmationLabel', { DELETE_CONFIRMATION_TEXT })}
                    name="confirmValue"
                />
            </>
        </Modal>
    );
};

export const useDeleteDealerModal = () => {
    const [open, setOpen] = useState(false);
    const validate = useValidator(deleteDealerValidator);

    const actions = useMemo(
        () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }),
        [setOpen]
    );

    return {
        ...actions,
        render: (onSubmit: () => void) => (
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="deleteDealerModalForm"
                        layout="vertical"
                        name="deleteDealerModalForm"
                        onSubmitCapture={handleSubmit}
                        requiredMark={false}
                    >
                        <DeleteDealerModal onClose={actions.close} open={open} />
                    </Form>
                )}
            </Formik>
        ),
    };
};

export default DeleteDealerModal;
